import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import api from "../../api/riderApi";
import StarRating from "../../components/StarRating";

export default function Earnings() {
  const [today, setToday] = useState(null);
  const [history, setHistory] = useState([]);
  const [myRating, setMyRating] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/earnings/today"),
      api.get("/earnings/history"),
      api.get("/my-rating"),
    ])
      .then(([t, h, r]) => {
        setToday(t.data);
        setHistory(h.data);
        setMyRating(r.data);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  const weekTotal = history
    .filter((s) => {
      const d = new Date(s.date);
      const now = new Date();
      const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      return d >= weekAgo;
    })
    .reduce((sum, s) => sum + parseFloat(s.total_earnings || 0), 0);

  const monthTotal = history
    .filter((s) => {
      const d = new Date(s.date);
      const now = new Date();
      return (
        d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear()
      );
    })
    .reduce((sum, s) => sum + parseFloat(s.total_earnings || 0), 0);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-blue-600 text-white px-5 pt-6 pb-8 rounded-b-3xl">
        <div className="flex items-center gap-3 mb-5">
          <Link to="/rider" className="text-blue-200">
            <svg
              className="w-6 h-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </Link>
          <h1 className="text-xl font-bold">Earnings</h1>
        </div>

        <div className="grid grid-cols-3 gap-3 text-center">
          <div className="bg-white/10 rounded-xl p-3">
            <p className="text-xs text-blue-200">Today</p>
            <p className="text-xl font-bold">
              ${(today?.total_earnings || 0).toFixed(2)}
            </p>
          </div>
          <div className="bg-white/10 rounded-xl p-3">
            <p className="text-xs text-blue-200">This Week</p>
            <p className="text-xl font-bold">${weekTotal.toFixed(2)}</p>
          </div>
          <div className="bg-white/10 rounded-xl p-3">
            <p className="text-xs text-blue-200">This Month</p>
            <p className="text-xl font-bold">${monthTotal.toFixed(2)}</p>
          </div>
        </div>
      </div>

      {/* My Rating Card */}
      {myRating && (
        <div className="px-5 -mt-4 mb-4">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
            <h2 className="text-sm font-semibold text-gray-500 mb-3">
              My Rating
            </h2>
            <div className="flex items-center gap-4">
              <div className="text-center">
                <p className="text-3xl font-bold text-gray-900">
                  {myRating.average_rating
                    ? parseFloat(myRating.average_rating).toFixed(1)
                    : "--"}
                </p>
                <div className="flex justify-center mt-1">
                  <StarRating
                    value={Math.round(myRating.average_rating || 0)}
                    readOnly
                    size={18}
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {myRating.total_ratings}{" "}
                  {myRating.total_ratings === 1 ? "review" : "reviews"}
                </p>
              </div>
              {myRating.recent_reviews.length > 0 && (
                <div className="flex-1 border-l border-gray-100 pl-4 space-y-2 max-h-32 overflow-y-auto">
                  {myRating.recent_reviews.slice(0, 3).map((review, i) => (
                    <div key={i} className="text-xs">
                      <div className="flex items-center gap-1">
                        <StarRating value={review.rating} readOnly size={12} />
                        <span className="text-gray-400">
                          {review.seller_name}
                        </span>
                      </div>
                      {review.comment && (
                        <p className="text-gray-600 mt-0.5">{review.comment}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Today's Breakdown */}
      {today && (
        <div className="px-5 -mt-4 mb-4">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
            <h2 className="text-sm font-semibold text-gray-500 mb-3">
              Today's Breakdown
            </h2>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Base pay</span>
                <span>${today.base_pay.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">
                  Commission ({today.deliveries_completed} deliveries)
                </span>
                <span>${today.commission.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">COD bonuses</span>
                <span>${today.cod_bonus.toFixed(2)}</span>
              </div>
              <div className="flex justify-between font-bold border-t pt-2 mt-2">
                <span>Total</span>
                <span className="text-blue-600">
                  ${today.total_earnings.toFixed(2)}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* History */}
      <div className="px-5 pb-8">
        <h2 className="text-sm font-semibold text-gray-500 mb-3">History</h2>
        <div className="space-y-2">
          {history.map((shift) => (
            <div
              key={shift.id}
              className="bg-white rounded-xl border border-gray-100 p-4 flex justify-between items-center"
            >
              <div>
                <p className="font-medium text-gray-900">
                  {new Date(shift.date).toLocaleDateString("en", {
                    weekday: "short",
                    month: "short",
                    day: "numeric",
                  })}
                </p>
                <p className="text-xs text-gray-500">
                  {shift.deliveries_completed} deliveries
                </p>
              </div>
              <p className="font-bold text-gray-900">
                ${parseFloat(shift.total_earnings).toFixed(2)}
              </p>
            </div>
          ))}
          {history.length === 0 && (
            <p className="text-center text-gray-400 py-8">
              No earnings history yet
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
